/**
 * Núcleo NÃO-VISUAL da alocação com prévia da JORNADA (§8.1.4 / M5 r1).
 *
 * ── A regra da jornada NÃO mora aqui ────────────────────────────────────────
 * Quais PERNAS um pallet percorre (`rack → cabeceira → transporte → cabeceira →
 * rack`), quantas OSs isso vira e onde ficam os HANDOFFS é decisão do BACKEND
 * (`POST /service-orders/allocation/preview`). Reimplementar isso no navegador
 * faria a tela prometer OSs diferentes das que o `POST /allocation` geraria —
 * exatamente o tipo de segunda verdade que a arquitetura do módulo evita. Este
 * arquivo só LÊ o que o servidor respondeu e organiza a bancada do Supervisor:
 * as LEVAS.
 *
 * ⚠️ O que mudou em 11/08 (M5-1): aéreo→aéreo deixou de ser erro. O planejador
 * não recusa mais nada por "empilhadeira só sobe e desce" — LIFT e TRANSPORT
 * são COMPORTAMENTOS, e o Supervisor pode dar a jornada inteira a UM
 * equipamento (`merge_into_single_order`). Por isso a prévia agora traz
 * `lines[].legs[]`, `staging_codes[]`, `handoffs[]` e `merged`.
 *
 * ── O que é uma LEVA ────────────────────────────────────────────────────────
 * "Fatiar" (§6.5) é o Supervisor dizer "isto aqui não vai tudo de uma vez".
 * Cada leva é um conjunto de pares LPN→destino que vira uma chamada de
 * alocação própria — logo, as OSs daquela jornada, com a sua rota. Fatiar antes
 * de confirmar é barato; desfazer OS gerada não é (fim de vida de OS é DONE ou
 * CANCELED, nunca DELETE).
 */

let sequence = 0;

function nextKey(prefix) {
    sequence += 1;
    return `${prefix}-${sequence}`;
}

/** Uma linha da bancada: um LPN e para onde ele vai. */
export function CreateAllocationRow(seed = {}) {
    return {
        key: nextKey('row'),
        lpn_code: seed.lpn_code ?? '',
        to_location_code: seed.to_location_code ?? '',
    };
}

/** Uma leva vazia (o estado inicial da tela tem exatamente uma). */
export function CreateAllocationBatch(rows = []) {
    return {
        key: nextKey('batch'),
        rows: rows.length > 0 ? rows : [CreateAllocationRow()],
        preview: null,
        previewError: null,
        previewing: false,
        routeAlgorithm: null,
        manualSequence: null,
        // Ordem produzida pelo método escolhido (códigos de LPN). Vira a ordem
        // de envio na confirmação — ver `OrderAllocationItems`.
        routeSequence: null,
        // A JORNADA INTEIRA numa OS só (M5-1): decisão do Supervisor, opção de
        // primeira classe. Vai NA PRÉVIA e na confirmação — a prévia tem de
        // mostrar exatamente as OSs que o `POST` vai gerar.
        mergeIntoSingleOrder: false,
        result: null,
        error: null,
    };
}

/** Linha COMPLETA = tem LPN e destino. Só ela vai no wire. */
export function IsCompleteRow(row) {
    return Boolean(row?.lpn_code?.trim()) && Boolean(row?.to_location_code?.trim());
}

/** Itens da leva no wire (`snake_case`, endereço por CÓDIGO como todo o M5). */
export function AllocationItemsOf(batch) {
    return (batch?.rows ?? []).filter(IsCompleteRow).map((row) => ({
        lpn_code: row.lpn_code.trim().toUpperCase(),
        to_location_code: row.to_location_code.trim().toUpperCase(),
    }));
}

/**
 * Aplica a ROTA ESCOLHIDA à ordem das linhas.
 *
 * ⚠️ Por que a ordem importa tanto: a confirmação da alocação NÃO tem campo de
 * sequência. O backend monta a jornada linha por linha, na ordem em que as
 * linhas chegam, e é essa ordem que vira a `seq` de cada item — ou seja, **a
 * ordem da rota que o operador vai executar**. Sem aplicar a escolha aqui,
 * escolher método no comparador não mudaria nada no que é gravado.
 *
 * (O que a `seq` NÃO é mais: a amarração do par. Desde o M5 r1 o vínculo é o
 * `handoff_from_item_id` — coluna explícita —, então reordenar linhas não corre
 * mais o risco de emparelhar itens errados.)
 *
 * `sequence` são códigos de LPN na ordem do método escolhido. Linha que não
 * aparece na sequência (ou sequência ausente/parcial) mantém a posição
 * original, no fim — nunca se perde uma linha por causa de reordenação.
 */
export function OrderAllocationItems(items, sequence) {
    if (!Array.isArray(sequence) || sequence.length === 0) {
        return items;
    }
    const rank = new Map();
    sequence.forEach((code, index) => {
        const key = String(code).trim().toUpperCase();
        if (key && !rank.has(key)) {
            rank.set(key, index);
        }
    });
    return items
        .map((item, index) => ({ item, index, rank: rank.get(item.lpn_code) ?? Number.POSITIVE_INFINITY }))
        .sort((left, right) => left.rank - right.rank || left.index - right.index)
        .map((entry) => entry.item);
}

/**
 * Fatia: move as linhas marcadas da leva de origem para uma leva NOVA, logo
 * depois dela. A leva de origem nunca fica vazia — tirar tudo dela seria
 * "renomear", não fatiar, e deixaria uma leva fantasma na tela.
 */
export function SplitAllocationBatch(batches, batchKey, selectedRowKeys) {
    const selected = new Set(selectedRowKeys);
    const index = batches.findIndex((batch) => batch.key === batchKey);
    if (index < 0 || selected.size === 0) {
        return batches;
    }
    const source = batches[index];
    const moved = source.rows.filter((row) => selected.has(row.key));
    const kept = source.rows.filter((row) => !selected.has(row.key));
    if (moved.length === 0 || kept.length === 0) {
        return batches;
    }
    // A leva de origem perdeu linhas: a prévia dela deixou de valer.
    const updatedSource = {
        ...source,
        rows: kept,
        preview: null,
        routeAlgorithm: null,
        manualSequence: null,
        routeSequence: null,
    };
    return [...batches.slice(0, index), updatedSource, CreateAllocationBatch(moved), ...batches.slice(index + 1)];
}

/** Substitui uma leva pelo resultado de `mutate` (lista imutável). */
export function UpdateBatch(batches, batchKey, mutate) {
    return batches.map((batch) => (batch.key === batchKey ? mutate(batch) : batch));
}

/**
 * As três PERNAS da jornada (M5 r1, regra 1). O número é o do contrato — é a
 * mesma numeração no documento, no planejador do backend e na tela.
 */
export const JOURNEY_LEGS = Object.freeze({ FROM_RACK: 1, HAUL: 2, TO_RACK: 3 });

function normalizeLeg(raw, index) {
    return {
        // `key` = "{linha}.{perna}" — a identidade da perna ANTES de o item
        // existir no banco. É por ela que o handoff aponta para quem entrega.
        key: raw?.key !== undefined && raw?.key !== null ? String(raw.key) : `leg-${index}`,
        leg: Number(raw?.leg ?? index + 1),
        behavior_key: raw?.behavior_key ?? null,
        from_location_code: raw?.from_location_code ?? null,
        to_location_code: raw?.to_location_code ?? null,
    };
}

function normalizeItem(raw, index) {
    return {
        seq: Number(raw?.seq ?? index + 1),
        key: raw?.key !== undefined && raw?.key !== null ? String(raw.key) : `item-${index}`,
        // De qual LINHA e de qual PERNA este item veio: é o que deixa a tela
        // dizer "pallet 2, perna 3 de 3" em vez de listar passos soltos.
        line_seq: raw?.line_seq === null || raw?.line_seq === undefined ? null : Number(raw.line_seq),
        leg: raw?.leg === null || raw?.leg === undefined ? null : Number(raw.leg),
        behavior_key: raw?.behavior_key ?? null,
        lpn_code: raw?.lpn_code ?? null,
        from_location_code: raw?.from_location_code ?? null,
        to_location_code: raw?.to_location_code ?? null,
        // Quem ENTREGA o pallet onde este item começa (null = ninguém: mesma OS
        // em sequência, ou primeira perna da jornada).
        handoff_from_key: raw?.handoff_from_key ?? null,
    };
}

function normalizeLine(raw, index) {
    return {
        seq: Number(raw?.seq ?? index + 1),
        lpn_code: raw?.lpn_code ?? null,
        direction: raw?.direction ?? null,
        split: raw?.split ?? null,
        from_location_code: raw?.from_location_code ?? null,
        to_location_code: raw?.to_location_code ?? null,
        // Uma jornada pode usar DUAS cabeceiras (rua de origem e de destino).
        staging_codes: Array.isArray(raw?.staging_codes) ? raw.staging_codes.filter(Boolean) : [],
        legs: (Array.isArray(raw?.legs) ? raw.legs : []).map(normalizeLeg),
    };
}

/**
 * Lê a resposta da PRÉVIA (M5 r1). Tolerância de ENVELOPE apenas (mesma
 * disciplina do `payload.js`): as chaves de dado são as do contrato —
 * `lines`/`legs`/`handoffs`/`service_orders`/`merged`/`paired`.
 *
 * ⚠️ O PAR vem sinalizado na RAIZ (`paired`) e `group_id` é NULO de propósito:
 * na prévia nada foi gravado, e devolver um id daria a entender que alguma
 * coisa foi reservada. `merged: true` = o Supervisor pediu a jornada inteira
 * numa OS só — e aí não existe handoff nenhum (é o mesmo operador em sequência).
 */
export function ParseAllocationPreview(payload) {
    const root = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
    const rawOrders = Array.isArray(payload)
        ? payload
        : Array.isArray(root.service_orders)
          ? root.service_orders
          : [];

    const orders = rawOrders.map((raw, index) => ({
        key: `preview-${index}`,
        type_code: raw?.type_code ?? null,
        type_label: raw?.type_label ?? raw?.type_code ?? null,
        behavior_key: raw?.behavior_key ?? null,
        status: raw?.status ?? null,
        items: (Array.isArray(raw?.items) ? raw.items : []).map(normalizeItem),
    }));

    const lines = (Array.isArray(root.lines) ? root.lines : []).map(normalizeLine);
    const handoffs = (Array.isArray(root.handoffs) ? root.handoffs : [])
        .map((raw) => ({
            from_key: raw?.from_key !== undefined && raw?.from_key !== null ? String(raw.from_key) : null,
            to_key: raw?.to_key !== undefined && raw?.to_key !== null ? String(raw.to_key) : null,
            location_code: raw?.location_code ?? null,
        }))
        .filter((handoff) => handoff.from_key && handoff.to_key);

    return {
        paired: root.paired === true,
        merged: root.merged === true,
        lines,
        handoffs,
        orders,
        staging_codes: [...new Set(lines.flatMap((line) => line.staging_codes))],
        warnings: Array.isArray(root.warnings) ? root.warnings : [],
    };
}

/**
 * `key` da perna → `{line, leg}`. É o que permite falar de um HANDOFF em
 * português ("o pallet PA-1, perna 1, entrega para a perna 2") em vez de
 * mostrar a chave crua ("1.1 → 1.2"), que só o backend precisa entender.
 */
export function IndexJourneyLegs(preview) {
    const index = new Map();
    for (const line of preview?.lines ?? []) {
        for (const leg of line.legs) {
            index.set(leg.key, { line, leg });
        }
    }
    return index;
}

/** Qual OS da prévia executa uma perna (pela `key` da perna). */
export function OrderIndexByLegKey(preview) {
    const index = new Map();
    for (const [orderIndex, order] of (preview?.orders ?? []).entries()) {
        for (const item of order.items) {
            index.set(item.key, orderIndex);
        }
    }
    return index;
}

/**
 * Itens que o comparador de rotas deve custear para esta leva.
 *
 * É a OS que ANDA PELAS RUAS: a vertical (`LIFT`) quando a jornada foi dividida
 * por comportamento; a única OS quando o Supervisor juntou tudo. Comparar rota
 * sobre o transporte horizontal quando existe uma OS vertical mostraria o
 * trecho errado — a decisão de rota do Supervisor é sobre a empilhadeira.
 *
 * A CHAVE do item no comparador é a `key` da perna ("{linha}.{perna}"), que é
 * estável e já é a identidade que o backend usa. A mesma jornada pode aparecer
 * com DUAS pernas (subir e descer) — são dois trabalhos reais e os dois entram
 * na conta.
 */
export function RouteItemsOfPreview(preview) {
    const orders = preview?.orders ?? [];
    const lift = orders.filter((order) => order.behavior_key === 'LIFT');
    const source = lift.length > 0 ? lift : orders;
    return source.flatMap((order) =>
        order.items
            .filter((item) => item.from_location_code && item.to_location_code)
            .map((item) => ({
                id: item.key,
                seq: item.seq,
                lpn_code: item.lpn_code,
                from_location_code: item.from_location_code,
                to_location_code: item.to_location_code,
            })),
    );
}
